/**
 * iPad: Rental Check-Out / Check-In inspection. Every item OK / ISSUE, gauges, cleanliness,
 * operator's manual, initials, and a camera photo for every required slot plus one per
 * attachment (D7: camera capture only). Submit is disabled until all of it is there, and the
 * server refuses it too. Works offline: each step is queued in order.
 */
import { useState } from 'react';
import { CLEANLINESS, CLEANLINESS_LABELS, rentalPhotoSlots } from '@wo/shared';
import type { Inspection } from '../lib/form-types';
import { enqueueFile, enqueueItem } from './queue';
import type { FormLists, MechForms, MechJob } from './types';
import { Big, BigField, CameraButton, Choice, Label, NoteBox, Sheet } from './ui';

type SetForms = (fn: (f: MechForms) => MechForms) => void;
type ItemState = { item: string; status: 'OK' | 'ISSUE' | null; comment: string };

export function RentalSheet({ job, lists, userId, setForms, onClose }: { job: MechJob; lists: FormLists; userId: number; setForms: SetForms; onClose: () => void }) {
  const r = job.forms.rental!;
  const mine: Inspection | null = r.phase === 'OUT' ? r.out : r.in;
  const locked = Boolean(mine?.submittedAt);
  const canEdit = !locked && (job.status === 'ASSIGNED' || job.status === 'IN_PROGRESS' || job.status === 'ON_HOLD');

  const [f, setF] = useState({
    hours: mine?.hours ?? '',
    fuelLevel: mine?.fuelLevel ?? null,
    fuelGallonsAdded: mine?.fuelGallonsAdded ?? '',
    defLevel: mine?.defLevel ?? null,
    defJugsAdded: mine?.defJugsAdded ?? '',
    cleanliness: mine?.cleanliness ?? null,
    operatorsManual: mine?.operatorsManual ?? null,
    initials: mine?.initials ?? '',
    notes: mine?.notes ?? '',
  });
  const [attachments, setAttachments] = useState<string[]>(mine?.attachments ?? []);
  const [newAttachment, setNewAttachment] = useState('');
  const [items, setItems] = useState<ItemState[]>(lists.rentalItems.map((item) => {
    const x = mine?.items.find((i) => i.item === item);
    return { item, status: x?.status ?? null, comment: x?.comment ?? '' };
  }));
  const [filled, setFilled] = useState<Set<string>>(new Set(mine?.photos.filter((p) => p.filled).map((p) => p.slot) ?? []));

  const slots = rentalPhotoSlots(lists.rentalPhotoSlots, attachments);
  const num = (v: string, frac: number) => v === '' || new RegExp(`^\\d{1,9}(\\.\\d{1,${frac}})?$`).test(v);
  const missing = [
    ...(f.hours === '' ? ['Machine hours'] : []),
    ...(!f.fuelLevel ? ['Fuel level'] : []),
    ...(!f.defLevel ? ['DEF level'] : []),
    ...items.filter((i) => !i.status || (i.status === 'ISSUE' && !i.comment.trim())).map((i) => i.item),
    ...(!f.cleanliness ? ['Cleanliness'] : []),
    ...(f.operatorsManual === null ? ["Operator's manual"] : []),
    ...(!f.initials.trim() ? ['Initials'] : []),
    ...slots.filter((s) => !filled.has(s)).map((s) => `Photo: ${s}`),
  ];
  const valid = num(f.hours, 1) && num(f.fuelGallonsAdded, 2) && num(f.defJugsAdded, 2);
  const label = r.phase === 'OUT' ? 'Check-Out' : 'Check-In';

  const payload = (atts = attachments) => ({
    type: 'rental_save' as const,
    workOrderId: job.id,
    ...f,
    hours: f.hours || null,
    fuelGallonsAdded: f.fuelGallonsAdded || null,
    defJugsAdded: f.defJugsAdded || null,
    initials: f.initials.trim() || null,
    attachments: atts,
    items: items.filter((i) => i.status).map((i) => ({ item: i.item, status: i.status!, comment: i.comment.trim() || null })),
  });

  const local = (submitted: boolean) =>
    setForms((x) => {
      const prev = x.rental!;
      const insp: Inspection = {
        ...(mine ?? { id: -1, workOrderId: job.id, phase: prev.phase, photos: [], missing: [] }),
        ...f,
        hours: f.hours || null,
        fuelGallonsAdded: f.fuelGallonsAdded || null,
        defJugsAdded: f.defJugsAdded || null,
        initials: f.initials || null,
        attachments,
        items: items.map((i) => ({ item: i.item, status: i.status, comment: i.comment || null })),
        photos: slots.map((slot) => ({ slot, filled: filled.has(slot), fileId: null, capturedAt: null })),
        submittedAt: submitted ? new Date().toISOString() : (mine?.submittedAt ?? null),
        missing,
      } as Inspection;
      return { ...x, rental: { ...prev, [prev.phase === 'OUT' ? 'out' : 'in']: insp } };
    });

  const save = async () => {
    await enqueueItem(userId, `${label} #${job.number}`, payload());
    local(false);
  };

  const photo = async (slot: string, file: File) => {
    // Attachment slots exist once the attachment is saved, so save first (queue keeps order).
    if (slot.startsWith('Attachment: ')) await enqueueItem(userId, `${label} attachments #${job.number}`, { type: 'rental_save', workOrderId: job.id, attachments });
    const photoClientId = await enqueueFile(userId, `${label} photo: ${slot}`, file, { workOrderId: job.id, kind: 'PHOTO', purpose: 'rental' });
    await enqueueItem(userId, `${label} photo: ${slot}`, { type: 'rental_photo', workOrderId: job.id, slot, photoClientId });
    setFilled((s) => new Set([...s, slot]));
  };

  const setItem = (i: number, patch: Partial<ItemState>) => setItems(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const other = r.phase === 'IN' ? r.out : null;

  return (
    <Sheet title={`Rental ${label} — #${job.number}`} onClose={onClose}>
      {r.phase === 'IN' && <p className="mb-2 text-lg">{r.checkoutNumber ? `Went out on #${r.checkoutNumber}.` : 'No check-out found for this machine — the office will pair it.'}</p>}
      {locked && <p className="mb-2 rounded-lg bg-green-100 p-3 text-lg text-green-900">Submitted. Ask the Manager if something needs fixing.</p>}
      {r.comparison && (r.comparison.hoursUsed || r.comparison.fuelUsed) && (
        <p className="mb-2 rounded-lg bg-slate-100 p-3 text-lg">Hours used: <b>{r.comparison.hoursUsed ?? '—'}</b> · Fuel: <b>{r.comparison.fuelUsed?.fraction ?? '—'}</b> · DEF: <b>{r.comparison.defUsed?.fraction ?? '—'}</b></p>
      )}

      <BigField label={`Machine hours${other?.hours ? ` (out: ${other.hours})` : ''}`} inputMode="decimal" value={f.hours} disabled={!canEdit} onChange={(e) => setF({ ...f, hours: e.target.value.replace(/[^\d.]/g, '') })} />
      <Label>Fuel level{other?.fuelLevel ? ` (out: ${other.fuelLevel})` : ''}</Label>
      <Choice options={lists.levels} value={f.fuelLevel} disabled={!canEdit} onChange={(v) => setF({ ...f, fuelLevel: v })} />
      <BigField label="Fuel gallons added" inputMode="decimal" value={f.fuelGallonsAdded} disabled={!canEdit} onChange={(e) => setF({ ...f, fuelGallonsAdded: e.target.value.replace(/[^\d.]/g, '') })} />
      <Label>DEF level{other?.defLevel ? ` (out: ${other.defLevel})` : ''}</Label>
      <Choice options={lists.levels} value={f.defLevel} disabled={!canEdit} onChange={(v) => setF({ ...f, defLevel: v })} />
      <BigField label="DEF jugs added" inputMode="decimal" value={f.defJugsAdded} disabled={!canEdit} onChange={(e) => setF({ ...f, defJugsAdded: e.target.value.replace(/[^\d.]/g, '') })} />

      <Label>Inspection</Label>
      <ul className="divide-y rounded-xl border-2 border-slate-200">
        {items.map((it, i) => {
          const was = other?.items.find((x) => x.item === it.item)?.status;
          return (
            <li key={it.item} className="p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex-1 text-lg">{it.item}{was && <span className="ml-2 text-base text-slate-500">(out: {was})</span>}</span>
                <Choice options={['OK', 'ISSUE'] as const} value={it.status} disabled={!canEdit} tones={{ ISSUE: 'stop' }} onChange={(v) => setItem(i, { status: v })} />
              </div>
              {it.status === 'ISSUE' && (
                <input className="mt-2 w-full rounded-xl border-2 border-red-400 p-3 text-lg" placeholder="Describe the issue (required) — 🎤 works here" autoCorrect="on" spellCheck value={it.comment} disabled={!canEdit} onChange={(e) => setItem(i, { comment: e.target.value })} />
              )}
            </li>
          );
        })}
      </ul>

      <Label>Cleanliness</Label>
      <Choice options={CLEANLINESS} value={f.cleanliness} labels={CLEANLINESS_LABELS} disabled={!canEdit} tones={{ ADDITIONAL_TIME: 'warn' }} onChange={(v) => setF({ ...f, cleanliness: v })} />
      <Label>Operator's manual</Label>
      <Choice options={['YES', 'NO'] as const} value={f.operatorsManual === null ? null : f.operatorsManual ? 'YES' : 'NO'} labels={{ YES: 'Yes', NO: 'No' }} disabled={!canEdit} onChange={(v) => setF({ ...f, operatorsManual: v === 'YES' })} />

      <Label>Attachments (each needs a photo)</Label>
      <div className="flex flex-wrap gap-2">
        {attachments.map((a) => (
          <Big key={a} tone="light" className="min-h-[56px] border-2 border-slate-300" disabled={!canEdit} onClick={() => setAttachments(attachments.filter((x) => x !== a))}>{a} ✕</Big>
        ))}
      </div>
      {canEdit && (
        <div className="mt-2 flex gap-2">
          <input className="flex-1 rounded-xl border-2 border-slate-400 p-3 text-lg" placeholder='e.g. Bucket 24"' value={newAttachment} onChange={(e) => setNewAttachment(e.target.value)} />
          <Big disabled={!newAttachment.trim() || attachments.includes(newAttachment.trim())} onClick={() => { setAttachments([...attachments, newAttachment.trim()]); setNewAttachment(''); }}>Add</Big>
        </div>
      )}

      <Label>Photos ({slots.filter((s) => filled.has(s)).length} of {slots.length})</Label>
      <div className="grid grid-cols-2 gap-2">
        {slots.map((slot) => (
          <CameraButton key={slot} tone={filled.has(slot) ? 'go' : 'light'} className="border-2 border-slate-300 text-left" disabled={!canEdit} onPhoto={(file) => void photo(slot, file)}>
            {filled.has(slot) ? `✓ ${slot}` : `📷 ${slot}`}
          </CameraButton>
        ))}
      </div>

      <BigField label="Initials" value={f.initials} maxLength={10} autoCapitalize="characters" disabled={!canEdit} onChange={(e) => setF({ ...f, initials: e.target.value })} />
      <Label>Notes</Label>
      <NoteBox value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} disabled={!canEdit} className="min-h-[120px]" />

      {canEdit && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Big disabled={!valid} onClick={() => void save().then(onClose)}>Save</Big>
          <Big tone="go" disabled={!valid || missing.length > 0} onClick={async () => {
            await enqueueItem(userId, `${label} #${job.number}`, payload());
            await enqueueItem(userId, `Submit ${label} #${job.number}`, { type: 'rental_submit', workOrderId: job.id });
            local(true);
            onClose();
          }}>Submit {label}</Big>
        </div>
      )}
      {canEdit && missing.length > 0 && <p className="mt-2 text-base text-slate-600">Still needed: {missing.slice(0, 6).join(', ')}{missing.length > 6 ? ` +${missing.length - 6} more` : ''}</p>}
    </Sheet>
  );
}
